import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const logo = `${process.env.PUBLIC_URL}/logo-original.png`;

const APP_SCHEME      = 'shiduchimapp';
const ANDROID_PACKAGE = 'com.zivugmishamayim';
const IOS_APP_STORE_ID = '6760430020';
const APP_STORE_URL    = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
const PLAY_STORE_URL   = 'https://play.google.com/store/apps/details?id=com.zivugmishamayim';

const IOS_STORE_FALLBACK_MS = 2000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Platform detection utility
const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMobile = isAndroid || isIOS;
  const isDesktop = !isMobile;
  
  return {
    isAndroid,
    isIOS,
    isMobile,
    isDesktop,
    userAgent: ua
  };
};

function ProfileSharePage() {
  const { profileId } = useParams();
  const isValid = useMemo(() => UUID_RE.test(profileId || ''), [profileId]);
  const iosStoreFallbackTimerRef = useRef(null);
  const [platform] = useState(detectPlatform);

  // View profile in web browser (for desktop users)
  const viewInBrowser = useCallback(() => {
    if (!isValid) return;
    // Redirect to external web app
    window.location.href = `https://app.zivugmishamayim.com/profile-details?id=${profileId}`;
  }, [isValid, profileId]);

  // Open in mobile app with deep link
  const openApp = useCallback(() => {
    if (!isValid) return;

    if (platform.isAndroid) {
      // Android Intent with automatic fallback to Play Store
      window.location.href =
        `intent://profile/${profileId}#Intent;scheme=${APP_SCHEME};` +
        `package=${ANDROID_PACKAGE};` +
        `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
      return;
    }

    // Clear any existing iOS fallback timer
    if (iosStoreFallbackTimerRef.current != null) {
      clearTimeout(iosStoreFallbackTimerRef.current);
      iosStoreFallbackTimerRef.current = null;
    }

    const started = Date.now();
    // Try to open the app via deep link
    window.location.href = `${APP_SCHEME}://profile/${profileId}`;

    // iOS: Set up fallback to App Store if app doesn't open
    if (platform.isIOS) {
      iosStoreFallbackTimerRef.current = setTimeout(() => {
        iosStoreFallbackTimerRef.current = null;
        // Only redirect if still on the page (app didn't open)
        if (Date.now() - started < IOS_STORE_FALLBACK_MS + 200 && !document.hidden) {
          window.location.href = APP_STORE_URL;
        }
      }, IOS_STORE_FALLBACK_MS);
    }
  }, [isValid, profileId, platform]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-open app on mobile devices
  useEffect(() => {
    if (!isValid || !platform.isMobile) return;
    
    // Slight delay before attempting to open the app
    const t = setTimeout(openApp, 150);
    
    return () => {
      clearTimeout(t);
      if (iosStoreFallbackTimerRef.current != null) {
        clearTimeout(iosStoreFallbackTimerRef.current);
        iosStoreFallbackTimerRef.current = null;
      }
    };
  }, [isValid, platform.isMobile, openApp]);

  return (
    <div className="App share-page">
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/" aria-label="Zivug Mishamayim home">
                <img src={logo} alt="Zivug Mishamayim" className="logo-image" />
              </Link>
            </div>
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/donate">Donate</Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="share-landing">
        <div className="share-card">
          <img src={logo} alt="Zivug Mishamayim" className="share-logo" />

          {isValid ? (
            <>
              <h1 className="share-title">Someone shared a profile with you</h1>
              
              {/* Desktop: Primary action is to view in browser */}
              {platform.isDesktop ? (
                <>
                  <p className="share-subtitle">
                    View the profile in your browser or download our mobile app for the full experience.
                  </p>
                  <button 
                    className="share-btn share-btn-primary" 
                    onClick={viewInBrowser}
                    style={{ marginBottom: '12px' }}
                  >
                    View Profile in Browser
                  </button>
                  <div className="share-divider">
                    <span>Or get the mobile app</span>
                  </div>
                </>
              ) : (
                /* Mobile: Primary action is to open in app */
                <>
                  <p className="share-subtitle">
                    Open it in the Zivug Mishamayim app to view the full profile.
                  </p>
                  <button 
                    className="share-btn share-btn-primary" 
                    onClick={openApp}
                    style={{ marginBottom: '12px' }}
                  >
                    Open in App
                  </button>
                  <div className="share-divider">
                    <span>Don't have the app yet?</span>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <h1 className="share-title">Invalid profile link</h1>
              <p className="share-subtitle">
                This link doesn't point to a valid profile. Download the app to browse profiles.
              </p>
            </>
          )}

          <div className="share-store-buttons">
            <button
              className="share-btn share-btn-store"
              disabled
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            >
              App Store - Coming Soon
            </button>
            <a
              href="https://play.google.com/store/apps/details?id=com.zivugmishamayim&pli=1"
              className="share-btn share-btn-store"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get it on Google Play
            </a>
          </div>

          <p className="share-footer-note">
            <Link to="/support">Support</Link>
            {' · '}
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default ProfileSharePage;
